import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { streamProxyUrl } from './api/client';

// Module-level Audio singleton — lives outside React so navigation never kills playback.
let _audio: HTMLAudioElement | null = null;

function getAudio(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;
  if (!_audio) {
    _audio = new Audio();
    _audio.preload = 'none';
    _audio.crossOrigin = 'anonymous'; // required for CORS mode so Access-Control-Allow-Origin is honoured
  }
  return _audio;
}

interface RadioPlayerState {
  channelSlug: string | null;
  channelName: string | null;
  /**
   * RAW upstream URL (e.g. `http://50.x.x.x:8000/stream`). NOT a pre-wrapped
   * `${baseUrl}/admin/stream-proxy?url=…` URL. We persist this raw value and
   * wrap with the *current* baseUrl at the moment we set `audio.src`, so a
   * VITE_API_URL change between deploys can't strand the player on the old
   * host. The von.muzi.ng → api.vonnews.ng migration regressed via that
   * exact path before this comment existed; see the `version` bump on the
   * persist config below for the one-shot eviction of the bad cached entry.
   */
  streamUrl: string | null;
  /** True iff the user *intends* the stream to be playing. May be false on a
   *  fresh page load even when persisted state had it true — see `pendingResume`. */
  playing: boolean;
  volume: number;
  muted: boolean;
  /** Non-null when the last play() attempt failed. Cleared on next play attempt. */
  error: string | null;
  /** True when persisted `playing` was true but autoplay was blocked on this load.
   *  Cleared as soon as the resume attempt succeeds (typically on first user gesture). */
  pendingResume: boolean;
  setChannel: (slug: string, name: string, streamUrl: string | null) => void;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  setVolume: (v: number) => void;
  toggleMute: () => void;
  /** Internal — called by hydration code paths AND by the global gesture listener. */
  attemptResume: () => void;
}

function describeError(err: unknown): string {
  const e = err as { name?: string; message?: string; code?: number };
  if (e?.name === 'NotAllowedError') return 'Autoplay blocked — click anywhere to resume';
  if (e?.name === 'NotSupportedError') return 'Stream format not supported';
  if (e?.name === 'AbortError') return 'Playback aborted';
  if (e?.message) return e.message;
  return 'Playback failed';
}

function wireAudioListeners(a: HTMLAudioElement, onError: (msg: string) => void) {
  a.onerror = () => {
    const codes: Record<number, string> = {
      1: 'Playback aborted',
      2: 'Network error — check the stream URL',
      3: 'Stream decoding failed',
      4: 'Stream format not supported by this browser',
    };
    onError(codes[a.error?.code ?? 0] ?? 'Unknown media error');
  };
}

export const useRadioPlayerStore = create<RadioPlayerState>()(
  persist(
    (set, get) => {
      const onAudioError = (msg: string) => set({ playing: false, error: msg });

      return {
        channelSlug: null,
        channelName: null,
        streamUrl: null,
        playing: false,
        volume: 0.8,
        muted: false,
        error: null,
        pendingResume: false,

        setChannel: (slug, name, streamUrl) => {
          const prev = get();
          if (slug === prev.channelSlug) {
            get().toggle();
            return;
          }
          const a = getAudio();
          if (!a) return;
          wireAudioListeners(a, onAudioError);
          a.pause();
          set({
            channelSlug: slug,
            channelName: name,
            streamUrl,
            error: null,
            pendingResume: false,
          });
          if (streamUrl) {
            a.src = streamProxyUrl(streamUrl);
            a.volume = prev.volume;
            a.muted = prev.muted;
            set({ playing: true });
            a.play().catch((err) => {
              console.error('[RadioPlayer]', err);
              set({ playing: false, error: describeError(err) });
            });
          } else {
            set({ playing: false });
          }
        },

        play: () => {
          const { streamUrl, volume, muted } = get();
          if (!streamUrl) return;
          const a = getAudio();
          if (!a) return;
          wireAudioListeners(a, onAudioError);
          const proxied = streamProxyUrl(streamUrl);
          if (a.src !== proxied) {
            a.src = proxied;
          }
          // Always sync volume + muted to the persisted store values —
          // not just on src change. Otherwise after a hard refresh the
          // audio element starts at HTMLMediaElement's default volume
          // (1.0) and ignores the slider position the user remembers.
          a.volume = volume;
          a.muted = muted;
          set({ playing: true, error: null, pendingResume: false });
          a.play().catch((err) => {
            console.error('[RadioPlayer]', err);
            set({ playing: false, error: describeError(err) });
          });
        },

        pause: () => {
          getAudio()?.pause();
          set({ playing: false, pendingResume: false });
        },

        toggle: () => {
          get().playing ? get().pause() : get().play();
        },

        setVolume: (v: number) => {
          const a = getAudio();
          if (a) { a.volume = v; a.muted = false; }
          set({ volume: v, muted: false });
        },

        toggleMute: () => {
          const { muted } = get();
          const a = getAudio();
          if (a) a.muted = !muted;
          set({ muted: !muted });
        },

        attemptResume: () => {
          const { streamUrl, volume, muted } = get();
          if (!streamUrl) {
            set({ pendingResume: false });
            return;
          }
          const a = getAudio();
          if (!a) return;
          wireAudioListeners(a, onAudioError);
          const proxied = streamProxyUrl(streamUrl);
          if (a.src !== proxied) {
            a.src = proxied;
          }
          // Same logic as play(): always reapply volume + muted from the
          // hydrated store so a refresh-then-resume picks up the user's
          // last slider position rather than the audio element's default.
          a.volume = volume;
          a.muted = muted;
          a.play()
            .then(() => set({ playing: true, pendingResume: false, error: null }))
            .catch((err) => {
              // Stay in pending state — user gesture listener will retry.
              if ((err as { name?: string }).name === 'NotAllowedError') {
                set({ playing: false, pendingResume: true, error: null });
              } else {
                console.error('[RadioPlayer] resume failed:', err);
                set({ playing: false, pendingResume: false, error: describeError(err) });
              }
            });
        },
      };
    },
    {
      name: 'von-admin-radio-player',
      storage: createJSONStorage(() => localStorage),
      // Bump when the persisted shape changes. v2 drops the entries from v1
      // (and pre-versioned entries from the von.muzi.ng → api.vonnews.ng era)
      // where `streamUrl` was the already-wrapped proxy URL with a baked-in
      // host. v2 stores the raw upstream URL and wraps at audio.src time.
      version: 2,
      // Only persist what's needed to recreate the playback state. `error` is
      // transient. `pendingResume` is recomputed at hydration time.
      partialize: (s) => ({
        channelSlug: s.channelSlug,
        channelName: s.channelName,
        streamUrl: s.streamUrl,
        playing: s.playing,
        volume: s.volume,
        muted: s.muted,
      }),
      onRehydrateStorage: () => (state) => {
        // Refresh wipes the live `<audio>` element — rebuild and try to play.
        // Browsers block autoplay without a recent user gesture, so we expect
        // this first attempt to fail; the global gesture listener (below) will
        // retry on the next click/keydown anywhere on the page.
        if (!state || !state.playing || !state.streamUrl) return;
        // Defer to the next tick so the store is fully constructed before we
        // call instance methods on it.
        setTimeout(() => {
          useRadioPlayerStore.getState().attemptResume();
        }, 0);
      },
    },
  ),
);

// Global one-shot-per-pending-resume gesture listener. Activates only when the
// store is in the `pendingResume` state — at all other times each handler is
// a single store-state read and a no-op. Capture-phase so we run before any
// component's onClick stops propagation.
//
// IMPORTANT: skip interactive controls. If the user clicks the radio player's
// own play button to "wake it up," the capture-phase pointerdown would call
// `attemptResume()` and flip `playing=true`, then the click handler's
// `store.toggle()` would immediately read that as "already playing" and pause
// it again — net effect, a flicker of audio that looks like autoplay is
// broken. Letting clicks on buttons / inputs fall through to their own
// handlers preserves the "click anywhere to resume" UX for non-interactive
// areas (nav, content, etc) while keeping the play button's own toggle
// semantics intact.
if (typeof window !== 'undefined') {
  const onGesture = (e: Event): void => {
    const s = useRadioPlayerStore.getState();
    if (!s.pendingResume) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest('button, a, input, select, textarea, [role="button"]')) {
      return;
    }
    s.attemptResume();
  };
  window.addEventListener('pointerdown', onGesture, { capture: true });
  window.addEventListener('keydown', onGesture, { capture: true });
}
